  import React, { useEffect, useRef } from 'react';
import offersBorder from '../../assets/images/offers-border.png';
import offersBg from '../../assets/images/offers-bg.png';
import offers1 from '../../assets/images/offers-1.png';
import offers2 from '../../assets/images/offers-2.png';
import offers3 from '../../assets/images/offers-3.png';
import offers4 from '../../assets/images/offers-4.png';
import offers5 from '../../assets/images/offers-5.png';
import offers6 from '../../assets/images/offers-6.png';

const ORDER_LINK = 'https://grill-me-crazy-indo-mexican-alice-springs.nextorder.com/';

const offers = [
  {
    day: 'MONDAY',
    name: 'Family Feast',
    nameColor: '#EC461C',
    description:
      'Bring everyone to the table with a feast made for sharing. Big flavours, generous portions, and the perfect way to start the week.',
    image: offers1,
  },
  {
    day: 'WEDNESDAY',
    name: 'Street Food Showdown',
    nameColor: '#EC461C',
    description:
      'Take a trip through the vibrant streets of India with your favourite chaats, paired with a refreshing drink.',
    image: offers2,
  },
  {
    day: 'THURSDAY',
    name: 'Taco-Tequila Day',
    nameColor: '#EC461C',
    description:
      'Bold tacos, refreshing margaritas, and the perfect excuse to celebrate almost Friday.',
    image: offers3,
  },
  {
    day: 'FRIDAY',
    name: 'Bollywood Nights',
    nameColor: '#EC461C',
    description:
      'Kick off the weekend with our signature Bollywood Bowl, perfectly paired with a glass of wine.',
    image: offers4,
  },
  {
    day: 'SATURDAY',
    name: 'Cantina Saturdays',
    nameColor: '#EC461C',
    description:
      'Gather your amigos for a Mexican feast made to share, complete with tacos, nachos, and margaritas.',
    image: offers5,
  },
  {
    day: 'SUNDAY',
    name: 'Lazy Sunday Feast',
    nameColor: '#EC461C',
    description:
      'Slow down, unwind, and end the week with hearty platters, cold beers, and great company.',
    image: offers6,
  },
];

const OffersSection: React.FC = () => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const paragraphRef = useRef<HTMLParagraphElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('animate-in');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = [headingRef.current, paragraphRef.current, gridRef.current, buttonRef.current];
    elements.forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return (
    <>
      <style>{`
        .offer-animate {
          opacity: 0;
          transform: translateY(40px);
          transition: opacity 0.8s ease-out, transform 0.8s ease-out;
        }
        .offer-animate.animate-in {
          opacity: 1;
          transform: translateY(0);
        }
        .offer-animate.delay-1 { transition-delay: 0.1s; }
        .offer-animate.delay-2 { transition-delay: 0.3s; }
        .offer-animate.delay-3 { transition-delay: 0.5s; }
        .offer-animate.delay-4 { transition-delay: 0.7s; }

        .offer-card {
          cursor: pointer;
          transition: transform 0.4s ease, box-shadow 0.4s ease;
          border-radius: 8px;
          overflow: hidden;
        }
        .offer-card:hover {
          transform: translateY(-8px) scale(1.02);
          box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        }
        .offer-card img {
          transition: transform 0.5s ease, filter 0.5s ease;
        }
        .offer-card:hover img {
          transform: scale(1.08);
          filter: brightness(1.1) saturate(1.2);
        }
        .offer-card .offer-info {
          transition: background-color 0.3s ease;
        }
        .offer-card:hover .offer-info {
          background-color: #fff8e8;
        }
        .offers-grid {
          display: grid;
          grid-template-columns: repeat(1, 1fr);
          gap: 16px;
        }
        @media (min-width: 640px) {
          .offers-grid {
            grid-template-columns: repeat(2, 1fr);
            gap: 20px;
          }
        }
        @media (min-width: 1024px) {
          .offers-grid {
            grid-template-columns: repeat(3, 1fr);
            gap: 24px;
          }
        }
      `}</style>

      <section id="offers" ref={sectionRef} className="w-full relative overflow-hidden">
        <div
          className="relative w-full"
          style={{
            maxWidth: '1920px',
            margin: '0px auto',
          }}
        >
          {/* Green Background Image */}
          <img
            src={offersBg}
            alt="Offers Background"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ display: 'block' }}
          />

          {/* Top Border */}
          <div className="relative z-10">
            <img
              src={offersBorder}
              alt="Offers Border"
              className="w-full block"
              style={{
                height: 'auto', position: 'relative',
                top: '-5px'
              }}
            />
          </div>

          {/* Content Overlay */}
          <div className="relative z-10 flex flex-col items-center" style={{ padding: '0 clamp(16px, 5vw, 100px)' }}>
            {/* Heading */}
            <h2
              ref={headingRef}
              className="offer-animate font-teko font-medium text-center mb-4 sm:mb-6"
              style={{ fontSize: 'clamp(28px, 7vw, 110px)', color: '#FFC600', marginTop: 'clamp(20px, 4vw, 60px)' }}
            >
              ONE CRAZY DEAL, EVERY SINGLE DAY
            </h2>

            {/* Paragraph */}
            <p
              ref={paragraphRef}
              className="offer-animate delay-1 text-center max-w-[1000px]"
              style={{
                fontSize: 'clamp(14px, 2.5vw, 29.5px)',
                color: '#FFF2DA',
                fontFamily: "'Inter', sans-serif",
                fontWeight: 400,
                lineHeight: '1.5',
                marginBottom: 'clamp(20px, 4vw, 60px)',
                padding: '0 0.5rem',
              }}
            >
              From family feasts and street food favourites to taco nights and Bollywood bowls,
              there's always something worth coming back for.
            </p>

            {/* Offers Grid - responsive columns */}
            <div
              ref={gridRef}
              className="offer-animate delay-2 offers-grid"
              style={{
                maxWidth: '1700px',
                width: '100%',
              }}
            >
              {offers.map((offer, index) => (
                <a
                  key={index}
                  href={ORDER_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="offer-card flex flex-col"
                  style={{
                    width: '100%',
                    textDecoration: 'none',
                    color: 'inherit',
                  }}
                >
                  {/* Offer Image */}
                  <img
                    src={offer.image}
                    alt={offer.name}
                    className="w-full"
                    style={{
                      height: 'clamp(200px, 30vw, 420px)',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />

                  {/* Info Section */}
                  <div
                    className="offer-info flex flex-col justify-start p-3 sm:p-5 flex-1"
                    style={{
                      backgroundColor: '#FFF2DA',
                    }}
                  >
                    {/* Day Label */}
                    <div
                      className="flex items-center justify-center mb-2 sm:mb-3"
                      style={{
                        width: 'clamp(60px, 8vw, 84px)',
                        height: 'clamp(22px, 3vw, 29px)',
                        backgroundColor: '#FFC600',
                        borderRadius: '50px',
                      }}
                    >
                      <span
                        className="font-teko text-center block leading-none"
                        style={{ fontSize: 'clamp(13px, 1.8vw, 18px)', color: '#151515', fontWeight: 400 }}
                      >
                        {offer.day}
                      </span>
                    </div>

                    {/* Offer Name */}
                    <h3
                      className="font-teko mb-1 sm:mb-2"
                      style={{
                        fontSize: 'clamp(24px, 4vw, 48px)',
                        color: offer.nameColor,
                        fontWeight: 400,
                        lineHeight: '1',
                      }}
                    >
                      {offer.name}
                    </h3>

                    {/* Description */}
                    <p
                      style={{
                        fontSize: 'clamp(13px, 1.8vw, 20px)',
                        fontFamily: "'Inter', sans-serif",
                        fontWeight: 400,
                        color: '#151515',
                        lineHeight: '1.4',
                      }}
                    >
                      {offer.description}
                    </p>
                  </div>
                </a>
              ))}
            </div>

            {/* Reserve A Table Button */}
            <div ref={buttonRef} className="offer-animate delay-3" style={{ marginTop: '60px', marginBottom: '60px' }}>
              <div
                style={{
                  boxShadow: '0 4px 0 0 #151515',
                  borderRadius: '8px',
                  display: 'inline-block',
                }}
              >
                <a
                  href={ORDER_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-teko font-normal text-black transition-all duration-200 hover:translate-y-[4px] hover:bg-yellow-400"
                  style={{
                    width: '194px',
                    height: '44px',
                    backgroundColor: '#FFC600',
                    border: '1px solid #151515',
                    borderRadius: '8px',
                    color: '#000000',
                    fontSize: '18px',
                    fontWeight: 500,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textDecoration: 'none',
                    cursor: 'pointer',
                  }}
                >
                  RESERVE A TABLE
                </a>
              </div>
            </div>
          </div>

          {/* Bottom Border */}
          <div className="relative z-10">
            <img
              src={offersBorder}
              alt="Offers Border"
              className="w-full block"
              style={{
                height: 'auto', position: 'relative',
                top: '10px'
              }}
            />
          </div>
        </div>
      </section>
    </>
  );
};

export default OffersSection;
